/**
 * RaceHeaderV2 Component
 * Dark NavHeader + RaceInfoBar matching Pencil k9lqA + 6slAL
 *
 * NavHeader: "LIVE RACE TRACKER" | Race name | Search + My Runners
 * InfoBar: Race name + date | Start/Distance/Elevation | LIVE badge + timer
 */

import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { Race, RunnerSummary } from '../../lib/types';
import { localToUTC, formatLocalDate } from '../../lib/timeFormatter';
import { formatInTimeZone } from 'date-fns-tz';
import { parseISO } from 'date-fns';
import { fetchActualRaceStart } from '../../lib/queries';
import { computeSimNow, type SimulationState } from '@as-checkin/shared';

interface RaceHeaderV2Props {
  race: Race;
  lastUpdated: Date | null;
  runners?: RunnerSummary[];
  onSearchToggle?: () => void;
  /** If provided, the My Runners button toggles a filter in place instead of linking to the standalone page. */
  myRunnersActive?: boolean;
  onToggleMyRunners?: () => void;
  /** AD-195: non-null while an RD is replaying this race. Drives the elapsed-time clock off the simulated instant instead of the real wall clock, and renders the SIMULATION chip. */
  simulation?: SimulationState | null;
}

function getRaceStartTimestamp(
  raceDate: string,
  raceStartTime: string | null,
  timeZone: string
): Date | null {
  if (!raceStartTime) return null;
  const localDateTime = `${raceDate}T${raceStartTime}`;
  const utcString = localToUTC(localDateTime, timeZone);
  if (!utcString) return null;
  return new Date(utcString);
}

function calculateRaceElapsedTime(
  actualStart: Date | null,
  scheduledStart: Date | null,
  nowOverride?: Date // AD-195: the simulated instant while simulating, in place of the real wall clock
): string | null {
  const startTime = actualStart || scheduledStart;
  if (!startTime) return null;

  const now = nowOverride ?? new Date();
  if (now < startTime) return null;

  const elapsedMs = now.getTime() - startTime.getTime();
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
}

function isRaceComplete(race: Race, runners?: RunnerSummary[]): boolean {
  if (race.status === 'completed') return true;
  if (runners && runners.length > 0) {
    return runners.every((r) => r.status === 'finished' || r.status === 'dropped');
  }
  return false;
}

function calculateFinalRaceTime(
  raceStartTimestamp: Date | null,
  runners?: RunnerSummary[]
): string | null {
  if (!raceStartTimestamp || !runners || runners.length === 0) return null;
  const finishedRunners = runners.filter((r) => r.status === 'finished' && r.finish_time);
  if (finishedRunners.length === 0) return null;

  const lastFinishTime = finishedRunners.reduce((latest, runner) => {
    if (!runner.finish_time) return latest;
    const finishTime = new Date(runner.finish_time).getTime();
    return finishTime > latest ? finishTime : latest;
  }, 0);

  if (lastFinishTime === 0) return null;

  const elapsedMs = lastFinishTime - raceStartTimestamp.getTime();
  const hours = Math.floor(elapsedMs / (1000 * 60 * 60));
  const minutes = Math.floor((elapsedMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((elapsedMs % (1000 * 60)) / 1000);

  return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
}

function hasRaceActuallyStarted(runners?: RunnerSummary[]): boolean {
  if (!runners || runners.length === 0) return false;
  const now = new Date();
  return runners.some((r) => {
    if (!r.race_start_time) return false;
    return new Date(r.race_start_time) <= now;
  });
}

export function RaceHeaderV2({ race, lastUpdated: _lastUpdated, runners, onSearchToggle, myRunnersActive, onToggleMyRunners, simulation }: RaceHeaderV2Props) {
  void _lastUpdated; // Available for future use (e.g., "Last updated X ago")
  const raceTimeZone = race.time_zone || 'America/Los_Angeles';
  const slug = race.slug;

  const raceStartTimestamp = useMemo(
    () => getRaceStartTimestamp(race.date, race.start_time, raceTimeZone),
    [race.date, race.start_time, raceTimeZone]
  );

  const raceComplete = useMemo(
    () => isRaceComplete(race, runners),
    [race.status, runners]
  );

  const [actualStartTime, setActualStartTime] = useState<Date | null>(null);

  const raceHasStarted = useMemo(
    () => hasRaceActuallyStarted(runners),
    [runners]
  );

  const [elapsedTime, setElapsedTime] = useState<string | null>(null);
  const [finalTime, setFinalTime] = useState<string | null>(null);

  useEffect(() => {
    async function loadActualStart() {
      if (race.status === 'active' || race.status === 'completed') {
        const startTimestamp = await fetchActualRaceStart(race.id);
        if (startTimestamp) setActualStartTime(new Date(startTimestamp));
      } else {
        setActualStartTime(null);
      }
    }
    loadActualStart();
  }, [race.id, race.status]);

  useEffect(() => {
    if (raceComplete) {
      const final = calculateFinalRaceTime(actualStartTime || raceStartTimestamp, runners);
      setFinalTime(final);
      return;
    }

    if (race.status === 'active') {
      // AD-195: while simulating, the elapsed readout tracks the simulated
      // instant (computeSimNow) instead of the viewer's real wall clock —
      // otherwise the clock would show "today" even though the replayed
      // events are timestamped on the race's own (possibly past/future) date.
      const initial = calculateRaceElapsedTime(actualStartTime, raceStartTimestamp, simulation ? computeSimNow(simulation) : undefined);
      setElapsedTime(initial);
    }

    const interval = setInterval(() => {
      if (race.status === 'active') {
        setElapsedTime(calculateRaceElapsedTime(actualStartTime, raceStartTimestamp, simulation ? computeSimNow(simulation) : undefined));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [race.status, raceComplete, actualStartTime, raceStartTimestamp, runners, simulation]);

  // Format start time for info bar
  const formattedStartTime = useMemo(() => {
    if (!race.start_time) return null;
    try {
      const localDateTime = `${race.date}T${race.start_time}`;
      const utcString = localToUTC(localDateTime, raceTimeZone);
      if (!utcString) return null;
      // AD-233 follow-up: append the short zone name (zzz → "MDT"/"MST",
      // daylight-aware for the race date) so every viewer can tell the
      // value is race-local time, not their own — e.g. "4:00 AM (MDT)".
      return formatInTimeZone(parseISO(utcString), raceTimeZone, 'h:mm a (zzz)');
    } catch {
      return null;
    }
  }, [race.date, race.start_time, raceTimeZone]);

  const formattedDate = useMemo(
    () => formatLocalDate(race.date, 'MMMM d, yyyy').toUpperCase(),
    [race.date]
  );

  return (
    <>
      {/* NavHeader - Pencil k9lqA */}
      <nav className="v2-nav">
        <div className="v2-nav-left">
          <span className="v2-nav-brand">LIVE RACE TRACKER</span>
          <div className="v2-nav-divider" />
          <span className="v2-nav-race-name">{race.name}</span>
        </div>
        <div className="v2-nav-right">
          {onSearchToggle && (
            <button className="v2-nav-search-icon" onClick={onSearchToggle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
          )}
          {onToggleMyRunners ? (
            <button
              type="button"
              className={`v2-nav-my-runners${myRunnersActive ? ' v2-nav-my-runners--active' : ''}`}
              onClick={onToggleMyRunners}
              aria-pressed={myRunnersActive}
              title={myRunnersActive ? 'Show all runners' : 'Show only my runners'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={myRunnersActive ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <span>My Runners</span>
            </button>
          ) : (
            <Link to={`/v2/${slug}/my-runners`} className="v2-nav-my-runners">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <span>My Runners</span>
            </Link>
          )}
        </div>
      </nav>

      {/* RaceInfoBar - Pencil 6slAL */}
      <div className="v2-info-bar">
        <div className="v2-info-bar-title">
          <div className="v2-info-bar-race-name">{race.name.toUpperCase()}</div>
          <div className="v2-info-bar-date">{formattedDate}</div>
        </div>

        <div className="v2-info-bar-stats">
          {formattedStartTime && (
            <div className="v2-info-stat">
              <span className="v2-info-stat-label">START TIME</span>
              <span className="v2-info-stat-value">{formattedStartTime}</span>
            </div>
          )}
          {race.distance && (
            <div className="v2-info-stat">
              <span className="v2-info-stat-label">DISTANCE</span>
              <span className="v2-info-stat-value">{race.distance.toUpperCase()}</span>
            </div>
          )}
        </div>

        <div className="v2-info-bar-live">
          {raceComplete ? (
            <>
              <div className="v2-complete-badge">
                <span className="v2-complete-text">COMPLETE</span>
              </div>
              {finalTime && (
                <span className="v2-race-timer">{finalTime}</span>
              )}
            </>
          ) : race.status === 'active' && (elapsedTime || raceHasStarted) ? (
            <>
              <div className="v2-live-badge">
                <div className="v2-live-dot" />
                <span className="v2-live-text">LIVE</span>
              </div>
              <span className="v2-race-timer">{elapsedTime || '...'}</span>
            </>
          ) : (
            <div className="v2-info-stat">
              <span className="v2-info-stat-label">STATUS</span>
              <span className="v2-info-stat-value">
                {race.status === 'draft' ? 'PENDING' : race.status.toUpperCase()}
              </span>
            </div>
          )}
          {/* AD-195: purple chip flags a replayed race — shown regardless of
              which branch above rendered, since a simulation can be scrubbed
              to any point (pending/live/complete) on the timeline. */}
          {simulation && (
            <span className="ml-2 rounded bg-purple-700 px-2 py-0.5 text-xs font-bold text-white">
              SIMULATION{simulation.playing ? ` ${simulation.speed}×` : ''}
            </span>
          )}
        </div>
      </div>
    </>
  );
}
